import { css } from "@emotion/react";
import { Theme, useTheme } from "@mui/material";
import { GetStaticPropsContext, GetStaticPropsResult } from "next";
import Image from "next/image";

import PageHead from "~/components/common/PageHead";
import {
  flexRow,
  mediaQueryIsDesktop,
  mediaQueryIsMobile,
} from "~/lib/client/design";
import useIsSpMode from "~/lib/client/hooks/useIsSpMode";
import { Layout } from "~/lib/client/layout";
import { formatDate } from "~/lib/graphql/graphqlClient";
import { convertMarkdownToHtml } from "~/lib/server/MarkdownHelper";
import {
  DiscoveryAd,
  fetchDiscoveryAd,
  fetchDiscoveryAds,
} from "~/lib/server/microcms";

type Ad = Omit<DiscoveryAd, "contentRaw"> & {
  convertedContent: string;
};

type Props = {
  ad: Ad;
};

export async function getStaticPaths() {
  const discoveryAds = await fetchDiscoveryAds();

  return {
    paths: discoveryAds.map((ad) => ({ params: { id: ad.id.toString() } })),
    fallback: "blocking",
  };
}

// FIXME
export async function getStaticProps(
  context: GetStaticPropsContext
): Promise<GetStaticPropsResult<Props>> {
  const id = context.params?.id;
  if (typeof id !== "string") {
    return {
      notFound: true,
    };
  }

  if (Number(id) == 0) {
    return {
      notFound: true,
    };
  }

  const discoveryAd = await fetchDiscoveryAd(id);
  const convertedContent = await convertMarkdownToHtml(discoveryAd.contentRaw);

  return {
    props: {
      ad: { ...discoveryAd, convertedContent },
    },
    revalidate: 60 * 60, // 1 hour
  };
}

const AdDetailPage = ({ ad }: Props) => {
  const isSpMode = useIsSpMode();
  const theme = useTheme();
  const Head = () => <PageHead title={ad.title} ogUrl={`/ad/${ad.id}`} />;
  const imageSize = isSpMode
    ? { width: 360, height: 180 }
    : { width: 1100, height: 550 };

  return (
    <Layout layoutType={"YTLayout"}>
      <Head />
      <main css={rootStyle(theme)}>
        <Image
          src={ad.image.url}
          alt={ad.title}
          width={imageSize.width}
          height={imageSize.height}
          key={ad.id}
          style={{ width: imageSize.width, height: imageSize.height }}
        />
        <article css={articleStyle(theme)}>
          <h1>{ad.title}</h1>
          <div dangerouslySetInnerHTML={{ __html: ad.convertedContent }}></div>
        </article>
        <div css={articleFooterStyle}>
          掲載期間: {formatDate(ad.startAt)} ~ {formatDate(ad.endAt)}
        </div>
      </main>
    </Layout>
  );
};

const rootStyle = (theme: Theme) => css`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;

  a {
    text-decoration: underline;
    color: ${theme.palette.primary.main};
  }

  h1 {
    font-size: 1.5rem;
    font-weight: 900;
  }
`;

const articleStyle = (theme: Theme) => css`
  ${mediaQueryIsDesktop(css`
    width: 1000px;
    padding: 2rem;

    img,
    video {
      width: 800px;
    }
  `)}

  ${mediaQueryIsMobile(css`
    width: 100vw;
    padding: 1.5rem;

    img {
      width: 95vw;
    }

    video {
      width: 90vw;
    }

    blockquote {
      margin-left: 0;
      padding-left: 1rem;
      border-left: 5px solid ${theme.palette.divider};
    }
  `)}

  white-space: pre-wrap;
  word-break: break-all;
`;

const articleFooterStyle = css`
  ${flexRow};
  width: 100%;
  align-items: flex-end;
  justify-content: flex-end;
  padding-top: 5rem;
`;

export default AdDetailPage;
